import { Alert, AlertDescription, AlertTitle, Button } from '@invent/wl-ui-kit-next/ui';
import { ArrowLeft, PenLine } from 'lucide-react';
import * as React from 'react';
import { Link, Navigate, useLocation, useMatch, useNavigate, useParams } from 'react-router-dom';

import { ParticipantAvatar, useRecordVisit } from '@/design';
import { useAuth, useCan } from '@/domains/auth';
import type {
  IAdminParticipantDetail,
  IAdminParticipantHub
} from '../../api/participant-contracts';
import { participantsApi } from '../../api/participants-api';
import {
  AdminPage,
  SectionTabs,
  type IAdminTab,
  LoadError,
  SectionFolds,
  TableSkeleton,
  useAdminData
} from '../../components/admin-page';
import {
  peopleSearchOf,
  personHubPath,
  RECORD_AREA_LABEL,
  RECORD_AREA_PATH,
  recordAccountPath,
  recordAreaOf,
  recordPathIn,
  RecordAreaContext,
  recordSurveyPath,
  searchAcross,
  openedFromResults,
  resultsListSearch,
  searchFrom,
  wayBack,
  type ICardLinkState,
  type RecordArea
} from '../../components/participant-link';
import { useListReread } from '../../components/list-area';
import { openFolds, SectionNav, type ISectionNavEntry } from '../../components/section-nav';
import { useDrawnDisclosures } from './disclosures';
import { HubHeader } from './header';
import { hubMembers } from './household';
import { HUB_MEMORY_PAGE } from './hub-memory';
import { hubSections, NOTES_ANCHOR, type IHubContext, type IHubSection } from './hub-sections';
import {
  AskEveryNumber,
  useAskEveryNumber,
  type IAskEveryNumber,
  useNumberNotes,
  type INumberWrites
} from './numbers';
import {
  ACCOUNT_TAB_LABEL,
  accountSections,
  ActionsMenu,
  NoSuchSurvey,
  RECORD_TAB_LABEL,
  recordLayoutOf,
  recordSections,
  type RecordLayout,
  recordView,
  SurveyStrip,
  surveyTabsOf,
  type RecordAction
} from './person-record';
import { recyclableSurveys, useRecycleAction } from './recycle-action';
import { useEscalate } from './escalate';
import type { IRosterRead } from './roster';
import type { IOpenItemsRead } from './work-items';
import { RecordSummary } from './record-summary';
import { teamMembers, useTeamNotes } from './team';

/**
 * One person, whole — the person hub (SR-215), over `GET /api/v1/admin/participants/{id}/hub`
 * under `responses.browse`, so staff and admin alike. What the product holds about a person or a
 * firm and everything an account of theirs acts for — the household: who they are, the numbers
 * and who verified them, what each registry last answered, the accounts acting for them with
 * whether those can sign in, their disclosures and the Salesforce tasks archived beside them
 * (SR-216), the registration runs that named them, their responses and staff's notes on them.
 * This folder holds it one file per section, listed once in `hub-sections.tsx` — the registry the
 * nav and the body are both drawn from, and the one place a later block adds its section; this
 * file is the route element, its one read and the poll over it, and the page's frame.
 *
 * **One scrolling column, a sticky section nav across its top** (R6 — the Team Portal study's
 * variant B, `section-nav.tsx`; a bar since the two portals, so the record keeps the width a
 * column beside it took once the People list sits beside the hub), under a header that answers
 * "who is this" (`header.tsx`). "Write a note", at the header's right, scrolls to the notes'
 * composer and puts the cursor in it — it navigates nowhere, for the section nav's reason. The
 * sections are in the assessment's order, and every one draws every member of the household,
 * the opened participant first (R7, `household.tsx`); the accounts are drawn once.
 *
 * **A person, a team and a firm open on their record page instead** (SR-391, `person-record.tsx`):
 * the same read, narrowed to the record (`recordView`), drawn as the record's tab beside one tab
 * per survey held, and an Account tab (the logins and registrations) — the tab is the address's
 * (`…/surveys/:code`, `…/account`, child routes, so this element
 * and its read stay mounted across tabs) — with "Ask registries again", "Write a note" and "New
 * task" (and a team's routing and archive) in one Actions menu (`useRecordActions`). Only an
 * `org` holding neither survey keeps the household's page below.
 *
 * **One read for the page, and it is the one that polls.** The hub document carries the
 * participant's card, the rest of its household, and the household's responses, notes and
 * pending lookups, all unpaged — so there is no second and third read to load and fail on its
 * own, and the responses and notes are paged here (`surveys.tsx`, `notes.tsx`). The old card's
 * three reads were the alternative: with one document, the re-read after a write brings back
 * the numbers, the responses and the notes of one moment. The activity is the first exception,
 * a read the server pages (`activity.tsx`): it has no bound, so it is read below a loaded hub
 * on its own, again after every write's re-read (`written`) and never on the poll. The
 * disclosures are the second (`disclosures.tsx`): survey-api serves them per participant, so each
 * member's are read below a loaded hub, and again only when the hub's summary of that member
 * changes — a poll tick that brings a BrokerCheck answer moves the summary, and the list follows
 * it; a tick that brings nothing re-reads nothing. The Salesforce tasks are the third
 * (`salesforce-tasks.tsx`): per member and paged by the server, read once — the archive changes
 * only when the ETL runs — and drawing a task's disclosure from what the Disclosures section drew.
 * The work items are the fourth (`work-items.tsx`, SR-222): each member's open items and the
 * household's history, read below a loaded hub, again on a write and on their own "Read again",
 * never on the poll.
 *
 * **Two writes on a number, both `responses.annotate`, both re-reading the hub after.**
 * "Ask BrokerCheck again" (E3) enqueues a registry lookup and verifies nothing; "Verify
 * number" (A6) stamps it by hand, for good. Each says what came back beside its own number —
 * a refusal, a press survey-api answered about another number, a write that landed and a
 * re-read that did not — because the page's error slot is far above a number and is cleared
 * by the next read. What is said is held here, per `(participantId, idType, value)`, not by the
 * button: a number the next read no longer carries takes its button with it, and the refusal
 * that explains why it went is shown under "Numbers no longer held here" instead; and the
 * header's "Ask registries again", which presses the first on every number of the household (of
 * the person, on their record page) in turn, says each answer in the same place (`numbers.tsx`).
 *
 * **While a lookup is waiting, the hub re-reads itself** — the one screen in the admin section
 * that polls, because it is the one place an answer is known to be on its way. It watches the
 * lookups the page draws — the household's `pendingLookups`, or a record's own on its record
 * page. Every five seconds, for two minutes after the page was opened or
 * a lookup was pressed, never while the tab is hidden, and quietly: `useAdminData` keeps the
 * page on screen while the next read is in flight, so nothing blanks, and a note half typed
 * below stays typed.
 *
 * **It renders the document and decides nothing about it**, with one exception survey-api
 * assigns to the client and one rule of the product's: numbers and registry answers are grouped
 * by registry (`registries.ts`), because the wire deliberately carries no `registry` member —
 * and a pending lookup is shown on the number it names, matched on `(idType, value)`; and
 * which numbers are registry numbers, offered "Verify number", is `VERIFIABLE_ID_TYPES`, the
 * owner's ruling rather than a mirror. The SEC ADV
 * register firms the numbers name (`secAdvFirms`, SR-214) are survey-api's match, drawn as sent
 * in a group of their own; which numbers it matches by is the one thing mirrored for them
 * (`SEC_ADV_MATCHED_ID_TYPES`). Nothing else is derived — not a verification from a
 * self-confirmation, from a registry's `ok` or from a register match, not a credential state.
 *
 * **Addressed by id, taken as React Router hands it over.** It is a GUID; a value that is not
 * one reaches survey-api's `guid` route constraint and is its 404, like an unknown one — this
 * page does not judge its shape.
 *
 * **Four arms, the user card's rule**: skeleton, load error, "no such participant", loaded.
 * `getHub` answers `null` for a 404 alone, and `useAdminData` leaves `data` null when the first
 * read throws — so a 403 or a 500 must not fall through to a claim that nobody is here. A later
 * read that fails leaves the page on screen as last read, and says so in those words rather
 * than as the first read's failure.
 *
 * **One hub per participant, remounted on the id.** This route's element is not remounted
 * when its parameter changes, and a household's members link from one hub straight to another —
 * so the page renders `PersonHub` keyed by the id. Without it the first participant stayed on
 * screen under the second one's address while its read was out, with its buttons live and
 * acting on the first participant, and a failed read printed its error over the wrong page.
 * Keyed, everything the page holds — its read, its re-read window, what it said beside a
 * number, its sections' page numbers — starts over with the participant.
 *
 * ## The accounts say what the document carries, and no more
 *
 * survey-api widened to the hub's audience part of what `accounts.browse` keeps — the login
 * state, so staff can answer "why can I not sign in" — and deliberately stopped short of the
 * identity. So the user card's Sign-in card is **not** composed here: its "not linked" reads
 * `idp === null`, and on this document `idp` is absent rather than null — it would state the one
 * fact this document withholds. For a viewer without `accounts.browse` the page says instead that
 * how the account signs in is on the user card. For one holding it, which opens that card, each
 * account reads it itself and draws the Sign-in card and the roles (R13, `credentials.tsx`) — a
 * read of its own per account, re-read with the activity after every write on the page but an
 * invitation sent, which re-reads its own list alone.
 */
export function AdminPersonHubPage({ area = 'people' }: { area?: RecordArea }) {
  const { participantId = '' } = useParams();
  const { state, search } = useLocation();
  // The search it was opened from, or — a hub whose own address carries the list's query — that.
  const from = searchFrom(state) || search;
  return <PersonHub key={participantId} participantId={participantId} search={from} area={area} />;
}

/**
 * The participant card's address, `/admin/participants/:participantId`, which every link made
 * before SR-215 points at: it lands on the hub **with its history state**, so the search a
 * link carried survives the hop and the hub's way back still returns to it — translated, since
 * it is the participant search's query and the way back is to People (`peopleSearchOf`). Any
 * other state is passed on as it came. The parameter is React Router's, already decoded, and is
 * encoded once for the new path — never decoded again.
 */
export function AdminParticipantCardRedirect() {
  const { participantId = '' } = useParams();
  const { state } = useLocation();
  const search = searchFrom(state);
  const carried: unknown = search
    ? ({ search: peopleSearchOf(search) } satisfies ICardLinkState)
    : state;
  return <Navigate to={personHubPath(participantId)} state={carried} replace />;
}

/**
 * The list a record page is drawn under: a person's People, a team's Teams, a firm's or org's
 * Firms (SR-414, the owner's Q2) — and the household's page, a non-person card without
 * `recordType`, People's.
 */
function canonicalArea(layout: Nullable<RecordLayout>): RecordArea {
  return layout === null ? 'people' : recordAreaOf({ recordType: layout });
}

/** The same tab of a record under another list — a survey's, the Account tab, or the record's. */
function recordTabPath(
  area: RecordArea,
  participantId: string,
  surveyCode: string | undefined,
  accountTab: boolean
): string {
  if (surveyCode) return recordSurveyPath(participantId, surveyCode, area);
  if (accountTab) return recordAccountPath(participantId, area);
  return recordPathIn(area, participantId);
}

function PersonHub({
  participantId,
  search,
  area
}: {
  participantId: string;
  search: string;
  /** The list the address put the page under — Teams, Firms or People (SR-414). */
  area: RecordArea;
}) {
  const { user } = useAuth();

  // Wrapped in an object for the user card's reason — `null` is "not loaded yet" to
  // `useAdminData`, and a bare `null` for "no such participant" would sit on a skeleton forever.
  // Each read is numbered as it begins, so a section holding a write's answer knows when the
  // document on screen comes from a read begun after that write landed (Veritant research's R9).
  const readsBegun = React.useRef(0);
  // A permission given or taken under the open page (the next `/auth/me`) reads the hub again,
  // with or without the staff fields it may now ask for.
  const canReadInternal = useCan()('internal_fields.read');
  const { data, error, reload } = useAdminData<{
    hub: Nullable<IAdminParticipantHub>;
    read: number;
  }>(
    async (signal) => {
      const read = ++readsBegun.current;
      return { hub: await participantsApi.getHub(participantId, canReadInternal, signal), read };
    },
    [participantId, canReadInternal]
  );
  const countReads = React.useCallback(() => readsBegun.current, []);

  const hub = data?.hub ?? null;
  const participant = hub?.participant ?? null;
  // A person, a team or a firm opens on its record page, its surveys as tabs (SR-391); any other
  // org on the household's page. The tab is the address's, under the hub's own route.
  const layout = participant ? recordLayoutOf(participant) : null;
  const recordPage = layout !== null;
  const list = RECORD_AREA_PATH[area];
  const surveyCode = useMatch(`${list}/:participantId/surveys/:surveyCode`)?.params.surveyCode;
  const accountTab = useMatch(`${list}/:participantId/account`) !== null;
  // A hub whose own address carries the list's query keeps it on every tab, so the way back and
  // Esc still reach the list as it was searched.
  const { search: ownSearch, state: ownState } = useLocation();
  const tabs =
    hub && layout
      ? [
          {
            to: `${recordPathIn(area, participantId)}${ownSearch}`,
            label: RECORD_TAB_LABEL[layout],
            end: true
          },
          { to: `${recordAccountPath(participantId, area)}${ownSearch}`, label: ACCOUNT_TAB_LABEL },
          ...surveyTabsOf(hub).map((tab) => ({
            to: `${recordSurveyPath(participantId, tab.code, area)}${ownSearch}`,
            label: tab.name
          }))
        ]
      : undefined;
  // Where the record belongs, once the hub has said what it is (the plan's D6): a link that could
  // not know the type — a work item's, a response's, a pasted one — named People.
  const home = participant ? canonicalArea(layout) : area;
  useRecordVisit(
    participant
      ? {
          kind: layout ?? 'org',
          path: recordPathIn(home, participant.participantId),
          label: participant.displayName
        }
      : null
  );
  // The lookups the page draws are the ones it waits on: a record's own on its record page, the
  // household's on its page.
  const waiting =
    participant && recordPage ? participant.pendingLookups : (hub?.pendingLookups ?? []);
  const rereads = useRereadWhilePending(waiting.length > 0, reload);

  // Every write re-reads the hub, and tells the activity to read again: a verify, a press, a
  // link or an unlink is an event of its own. The poll re-reads the hub alone.
  // It also tells the list beside it (SR-530): the row is marked dealt with in its column.
  const [written, setWritten] = React.useState(0);
  const rereadList = useListReread();
  const rereadAfterWrite = React.useCallback(() => {
    setWritten((count) => count + 1);
    rereadList();
    return reload();
  }, [reload, rereadList]);

  // A press on a lookup — asking again, or checking by hand — is what restarts the window. Asking
  // again is a write, re-read as every write is; checking by hand is the poll's twin, and re-reads
  // the hub alone, as a tick does — nothing was written, so the activity has nothing new (R12).
  const recheck = async () => {
    rereads.restart();
    return rereadAfterWrite();
  };
  const checkAgain = async () => {
    rereads.restart();
    return reload();
  };

  // A record under another list's address takes its own — the same tab, and the history state
  // carried, so its way back is the one it was opened with (the owner's Q2). The search is the
  // other list's, so only its `q` crosses (`searchAcross`), and the record is not drawn beside a
  // list it was not opened from. The element under the new address reads the hub again: one more
  // read, as a participant change is.
  const back = wayBack(ownState, list, RECORD_AREA_LABEL[area], search);
  if (home !== area) {
    const to = recordTabPath(home, participantId, surveyCode, accountTab);
    // The results page's search goes as it is: the way back is that page, not another list.
    const carried = openedFromResults(ownState)
      ? searchFrom(ownState)
      : searchAcross(searchFrom(ownState), area, home);
    const state: unknown =
      ownState !== null && typeof ownState === 'object'
        ? { ...(ownState as object), search: carried, list: undefined }
        : ownState;
    return (
      <Navigate to={`${to}${searchAcross(ownSearch, area, home)}`} state={state} replace />
    );
  }

  return (
    // Every record link on the page knows which list it is drawn under, so a link to a record of
    // another list carries the search's `q` alone (`searchAcross`).
    <RecordAreaContext.Provider value={area}>
      <AdminPage
        title={participant?.displayName ?? 'Participant'}
        crumb={
          // The way back is the crumb, carrying the search the hub was opened from (SR-407) — a
          // second "← All people" link under the title said the same.
          <Link
            to={back.to}
            className="inline-flex items-center gap-1 text-sm text-[var(--color-sapphire)] underline-offset-4 hover:underline"
          >
            <ArrowLeft className="size-3.5" aria-hidden />
            {back.words}
          </Link>
        }
        media={
          participant && (
            <ParticipantAvatar
              photoUri={participant.photoUri}
              displayName={participant.displayName}
              size="lg"
              decorative
            />
          )
        }
        actions={
          hub &&
          !recordPage && (
            <Button type="button" size="sm" onClick={writeNote} className="gap-1.5">
              <PenLine className="size-4" aria-hidden />
              Write a note
            </Button>
          )
        }
      >
        {error &&
          (data === null ? (
            <LoadError message={error} />
          ) : (
            // A page already on screen stays, as last read: the numbers and what was said beside
            // them are still true of that read, and a blank would take them away for a blip.
            <Alert variant="destructive">
              <AlertTitle>Could not refresh — showing the page as last read</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ))}

        {data === null ? (
          error ? null : <TableSkeleton rows={4} />
        ) : hub && participant ? (
          <HubBody
            hub={hub}
            search={openedFromResults(ownState) ? resultsListSearch(search) : search}
            callerEmail={user?.email ?? ''}
            canReadInternal={canReadInternal}
            watching={rereads.watching}
            onRecheck={recheck}
            onCheckAgain={checkAgain}
            reread={rereadAfterWrite}
            written={written}
            hubRead={data.read}
            readsBegun={countReads}
            layout={layout}
            tabNav={tabs}
            surveyCode={recordPage ? surveyCode : undefined}
            accountTab={recordPage && accountTab}
          />
        ) : (
          <Alert>
            <AlertTitle>No such participant</AlertTitle>
            <AlertDescription>
              Nothing on this server is held under <strong>{participantId}</strong>.
            </AlertDescription>
          </Alert>
        )}
      </AdminPage>
    </RecordAreaContext.Provider>
  );
}

/** "Write a note": to the notes' composer, the cursor in its box — or, for a viewer without
 *  `responses.annotate`, whose box is drawn off, the Notes fold's own summary: a disabled box takes
 *  no focus, and the press still lands somewhere that says what the section is. The composer's
 *  `NotPermitted` sentence sits under it. */
function writeNote() {
  const notes = document.getElementById(NOTES_ANCHOR);
  if (!notes) return;
  openFolds(notes);
  // The box is behind "New note" (SR-407): the press opens it, and the box, drawn after the press
  // returns, takes the cursor itself (`autoFocus`); the query below finds the summary meanwhile.
  notes.querySelector<HTMLButtonElement>('[data-new-note]')?.click();
  notes.scrollIntoView({ block: 'start' });
  const target =
    notes.querySelector<HTMLElement>('textarea:not(:disabled)') ??
    notes.querySelector<HTMLElement>(':scope > summary');
  target?.focus({ preventScroll: true });
}

function HubBody({
  hub,
  search,
  callerEmail,
  canReadInternal,
  watching,
  onRecheck,
  onCheckAgain,
  reread,
  written,
  hubRead,
  readsBegun,
  layout,
  tabNav,
  surveyCode,
  accountTab
}: {
  hub: IAdminParticipantHub;
  search: string;
  callerEmail: string;
  canReadInternal: boolean;
  watching: boolean;
  onRecheck: () => Promise<boolean>;
  onCheckAgain: () => Promise<boolean>;
  reread: () => Promise<boolean>;
  written: number;
  hubRead: number;
  readsBegun: () => number;
  /** The record page drawn (SR-391), or null for the household's page. */
  layout: Nullable<RecordLayout>;
  /** The record page's routed tabs, drawn under the record card (SR-406) — what is above them is
   *  the record's, the same on every tab; what is below, the tab's. */
  tabNav: IAdminTab[] | undefined;
  /** The survey tab the address names, on a record page. */
  surveyCode: string | undefined;
  /** The Account tab is the one the address names (SR-391). */
  accountTab: boolean;
}) {
  // `responses.annotate` — the numbers' presses and the header's (SR-228).
  const canAnnotate = useCan()('responses.annotate');
  // A record page draws the record alone (`recordView`); the household's page the hub as read.
  const recordPage = layout !== null;
  const shown = React.useMemo(() => (recordPage ? recordView(hub) : hub), [recordPage, hub]);
  const members = hubMembers(shown);
  const { said, say } = useNumberNotes(shown);
  const asking = useAskEveryNumber(members, say, onRecheck);
  const teamNotes = useTeamNotes();
  const drawnDisclosures = useDrawnDisclosures(shown);
  const tabs = recordPage ? surveyTabsOf(shown) : [];
  const tab = surveyCode === undefined ? undefined : tabs.find((each) => each.code === surveyCode);
  const recordTo = recordPathIn(canonicalArea(layout), hub.participant.participantId);
  const actions = useRecordActions(surveyCode !== undefined || accountTab, recordTo, asking);
  // SR-490: the menu's way to a survey strip's recycle press.
  const recyclable = React.useMemo(
    () => (recordPage ? recyclableSurveys(shown) : []),
    [recordPage, shown]
  );
  const recycle = useRecycleAction(
    recyclable,
    (code) => recordSurveyPath(hub.participant.participantId, code, canonicalArea(layout)),
    surveyCode
  );
  const canDecide = useCan()('work_items.decide');
  const escalation = useEscalate(
    hub.participant.participantId,
    hub.participant.displayName,
    reread,
    canAnnotate
  );
  const { search: here, hash } = useLocation();
  useLandOnHash(hash);
  // Each roster part's read, as the Advisors section hands it up — the summary's lead (SR-400).
  const [rosters, onRoster] = useHandedUp<IRosterRead>();
  // And each member's open work items, as Work items reads them — the summary's compliance line.
  const [openItems, onOpenItems] = useHandedUp<IOpenItemsRead>();
  // The feed's total as its own read last landed — the nav's Activity count (F11's Q3), handed up
  // by the section rather than read a second time here.
  const [activityTotal, setActivityTotal] = React.useState<number>();
  // The survey tab's staff-field sections, as its Veritant research drew them (SR-474) — kept with
  // the tab they were drawn on, so another tab's never names this one's.
  const [research, setResearch] = React.useState<{ code: string; entries: ISectionNavEntry[] }>();
  const onResearchEntries = React.useCallback(
    (entries: ISectionNavEntry[]) => {
      if (tab) setResearch({ code: tab.code, entries });
    },
    [tab?.code]
  );
  const writes: INumberWrites = {
    said,
    say,
    askingAll: asking.askingAll,
    callerEmail,
    watching,
    onRecheck,
    onCheckAgain,
    onVerified: reread,
    permitted: canAnnotate
  };
  const ctx: IHubContext = {
    hub: shown,
    asRead: hub,
    members,
    search,
    canReadInternal,
    written,
    reread,
    hubRead,
    readsBegun,
    writes,
    teams: teamMembers(members, teamNotes),
    teamNotes,
    drawnDisclosures,
    crdName: (participantId, crd) =>
      ownCrdName(members, participantId, crd) ?? rosterName(rosters[participantId], crd),
    withDisclosures: shown.disclosures.some((entry) => entry.summary !== null),
    activityTotal,
    onActivityTotal: setActivityTotal,
    recordPage,
    surveyCode: tab?.code,
    newTaskAsked: actions.newTaskAsked,
    onRoster,
    onOpenItems,
    openItems,
    researchEntries: research !== undefined && research.code === tab?.code ? research.entries : null,
    onResearchEntries
  };
  // One list for the nav and the body, so the two cannot disagree (`hub-sections.tsx`).
  const drawn = (list: IHubSection[]) => list.filter((section) => section.drawn?.(ctx) ?? true);
  // A record tab stays mounted, hidden, while another tab is shown: its drafts (a note
  // half typed), what its writes were told and its page numbers are kept, and the sections read
  // once (Records, Salesforce tasks) are not read again on the way back. The survey tab drawn is
  // the one the address names, remounted per survey.
  const recordTab = drawn(layout ? recordSections(layout, undefined) : hubSections());
  const surveySections = tab && layout ? drawn(recordSections(layout, tab.code)) : [];
  const unknownTab = surveyCode !== undefined && tab === undefined;
  const onRecord = !tab && !unknownTab && !accountTab;
  // The Account tab is kept mounted like the record's: an issued reset token is shown once, and a
  // tab press must not take it away.
  const accountTabSections = recordPage ? drawn(accountSections()) : [];
  const navSections = tab ? surveySections : accountTab ? accountTabSections : recordTab;
  const routed = hub.participant.teamRouting !== null;
  const archived = hub.participant.archive !== null;
  const teamActions = { routed, drawn: routed || archived, archived };
  const header = (
    <HubHeader
      hub={shown}
      reread={reread}
      recordPage={recordPage}
      actions={
        recordPage ? (
          <div className="flex justify-end">
            <ActionsMenu
              asking={asking}
              canAnnotate={canAnnotate}
              canDecide={canDecide}
              onAct={(action) =>
                action === 'escalate'
                  ? escalation.open()
                  : action === 'recycle'
                    ? recycle.start()
                    : actions.act(action)
              }
              team={layout === 'team' ? teamActions : undefined}
              recyclable={recyclable.length}
            />
            {recycle.chooser}
          </div>
        ) : (
          <AskEveryNumber asking={asking} permitted={canAnnotate} />
        )
      }
      summaryPanel={
        layout && (
          <RecordSummary
            participant={shown.participant}
            layout={layout}
            tabs={tabs}
            search={here}
            written={written}
            roster={rosters[shown.participant.participantId]}
            openItems={openItems[shown.participant.participantId]}
            canReadInternal={canReadInternal}
            affiliations={shown.affiliations}
          />
        )
      }
    />
  );
  return (
    <>
      {header}
      {tabNav && (
        <div className="border-b border-border">
          <SectionTabs tabs={tabNav} />
        </div>
      )}
      {escalation.dialog}
      {unknownTab && layout && (
        <NoSuchSurvey code={surveyCode} recordTo={recordTo} layout={layout} />
      )}
      {tab && <SurveyStrip hub={shown} tab={tab} reread={reread} />}
      {!unknownTab && (
        <SectionNav
          entries={navSections.flatMap(
            ({ anchor, label, count, entries }) =>
              entries?.(ctx) ?? [{ anchor, label, count: count?.(ctx) }]
          )}
        />
      )}
      {/* Every section a fold, remembered under the hub's one memory page (F11's Q2, R3). */}
      <SectionFolds.Provider value={HUB_MEMORY_PAGE}>
        <div hidden={!onRecord} className={onRecord ? 'grid min-w-0 gap-6' : 'hidden'}>
          {recordTab.map((section) => (
            <React.Fragment key={section.key}>{section.render(ctx, section.anchor)}</React.Fragment>
          ))}
        </div>
        {recordPage && (
          <div hidden={!accountTab} className={accountTab ? 'grid min-w-0 gap-6' : 'hidden'}>
            {accountTabSections.map((section) => (
              <React.Fragment key={section.key}>
                {section.render(ctx, section.anchor)}
              </React.Fragment>
            ))}
          </div>
        )}
        {tab && (
          <div key={tab.code} className="grid min-w-0 gap-6">
            {surveySections.map((section) => (
              <React.Fragment key={section.key}>
                {section.render(ctx, section.anchor)}
              </React.Fragment>
            ))}
          </div>
        )}
      </SectionFolds.Provider>
    </>
  );
}

/**
 * The Actions menu's presses (SR-391): each runs on the record tab, where what it answers is said
 * — so a press on another tab (a survey's, the Account tab) goes there first and runs once the
 * record's sections are drawn.
 * "Ask registries again" is the header's old button; "Write a note" its page action; "New task"
 * the opened participant's own form in Work items, opened with the cursor in its subject.
 */
function useRecordActions(
  offRecordTab: boolean,
  recordTo: string,
  asking: Pick<IAskEveryNumber, 'askAll'>
): { act: (action: RecordAction) => void; newTaskAsked: number } {
  const navigate = useNavigate();
  const { state, search } = useLocation();
  const [waiting, setWaiting] = React.useState<Nullable<RecordAction>>(null);
  const [newTaskAsked, setNewTaskAsked] = React.useState(0);
  const askAll = React.useRef(asking.askAll);
  askAll.current = asking.askAll;

  React.useEffect(() => {
    if (waiting === null || offRecordTab) return;
    setWaiting(null);
    if (waiting === 'note') writeNote();
    if (waiting === 'ask') void askAll.current();
    if (waiting === 'task') {
      const items = document.getElementById('work-items');
      if (items) openFolds(items);
      setNewTaskAsked((count) => count + 1);
    }
    // A team's two writes are the Team section's controls, with their confirmation and rules: the
    // press puts the reader on the control, as "Write a note" puts them in the note's box.
    if (waiting === 'routing') focusInTeam('Veritant’s decision');
    if (waiting === 'archive') focusInTeam('Archive');
  }, [waiting, offRecordTab]);

  const act = (action: RecordAction) => {
    if (offRecordTab) navigate(`${recordTo}${search}`, { state });
    setWaiting(action);
  };
  return { act, newTaskAsked };
}

/** A person's own name on a CRD group whose number is one of their own `CRD` identifiers, as
 *  stored — compared as spelled, never canonicalised here, so a number held in another spelling
 *  is headed by the CRD alone rather than claimed. Anyone else's CRD in their set (one the ETL
 *  filed under their Salesforce record) is named by Salesforce, or not at all. */
function ownCrdName(
  members: IAdminParticipantDetail[],
  participantId: string,
  crd: string
): Nullable<string> {
  const member = members.find((each) => each.participantId === participantId);
  if (member?.kind !== 'person') return null;
  const own = member.identifiers.some(
    (identifier) => identifier.idType === 'CRD' && identifier.value === crd
  );
  return own ? member.displayName : null;
}

/** The name a roster's slot holding `crd` answers to — the survey's answer, else the person the
 *  slot resolves to — or null: no roster read, no slot with that CRD, or a slot naming nobody. */
function rosterName(read: IRosterRead | undefined, crd: string): Nullable<string> {
  const slot = read?.roster?.members.find((member) => member.crd === crd);
  return slot?.fullName ?? slot?.member?.displayName ?? null;
}

/** A read a section hands up as it lands, held per participant — what the record summary says
 *  without reading it a second time (SR-400). */
function useHandedUp<T>(): [Record<string, T>, (participantId: string, read: T) => void] {
  const [held, setHeld] = React.useState<Record<string, T>>({});
  const hand = React.useCallback((participantId: string, read: T) => {
    setHeld((before) => ({ ...before, [participantId]: read }));
  }, []);
  return [held, hand];
}

/** Opens the Team section and focuses the first control of one of its parts (its `aria-label`). */
function focusInTeam(part: string) {
  const team = document.getElementById('team');
  if (!team) return;
  openFolds(team);
  // The decision's form is behind a press (SR-407): pressed here, it takes the cursor itself once
  // drawn (`TeamPart`), so this only brings it into view.
  const opener = team.querySelector<HTMLButtonElement>(
    `section[aria-label="${part}"] [data-open-decision]`
  );
  if (opener) {
    opener.click();
    opener.scrollIntoView({ block: 'center' });
    return;
  }
  const control = team.querySelector<HTMLElement>(
    `section[aria-label="${part}"] :is(button, input, [role="radio"]):not(:disabled)`
  );
  (control ?? team.querySelector<HTMLElement>(':scope > summary'))?.focus();
  (control ?? team).scrollIntoView({ block: 'center' });
}

/** How often, and for how long after the page was opened or a lookup pressed, a hub with a
 *  lookup waiting re-reads itself (R10). Long enough for a registry that answers in seconds,
 *  short enough that a page left open over lunch stops asking. */
const REREAD_EVERY_MS = 5_000;
const REREAD_FOR_MS = 2 * 60_000;

/**
 * The hub's own re-reads while an answer is on its way — every `REREAD_EVERY_MS`, until
 * nothing is pending or `REREAD_FOR_MS` has passed since the page was opened or `restart` was
 * last called, skipping a tick while the tab is hidden or the last re-read is still in flight.
 *
 * `watching` says which of the two it is, so the page can say it has stopped rather than
 * leave a reader waiting on a read that is no longer coming. Nothing else in the admin section
 * polls: no other screen has an answer it knows to be on its way. The disclosures re-read with it
 * only when a tick moves a member's summary — a re-read keyed on the document, not on a clock —
 * and nothing else on the page does: the activity and the work items re-read on a write, the
 * Salesforce tasks never.
 */
function useRereadWhilePending(
  pending: boolean,
  reread: () => Promise<unknown>
): { watching: boolean; restart: () => void } {
  const [since, setSince] = React.useState(() => Date.now());
  const [expired, setExpired] = React.useState(false);
  const rereadRef = React.useRef(reread);
  rereadRef.current = reread;

  React.useEffect(() => {
    setExpired(false);
    if (!pending) return;
    let inFlight = false;
    const timer = window.setInterval(() => {
      if (Date.now() - since >= REREAD_FOR_MS) {
        window.clearInterval(timer);
        setExpired(true);
        return;
      }
      if (inFlight || document.visibilityState === 'hidden') return;
      inFlight = true;
      void rereadRef.current().finally(() => {
        inFlight = false;
      });
    }, REREAD_EVERY_MS);
    return () => window.clearInterval(timer);
  }, [pending, since]);

  const restart = React.useCallback(() => setSince(Date.now()), []);
  return { watching: pending && !expired, restart };
}

/** How long a fragment waits for its section to be drawn, and then keeps it in place while the
 *  sections above it land (the answers read after the record). */
const HASH_WAIT_MS = 8000;
const HASH_HOLD_MS = 4000;

/**
 * A fragment in the address lands on its section once the section is drawn (SR-412): Lists'
 * *Edit* in a new tab opens a survey's record at `#research`, the staff fields — the first section
 * whose id is the fragment or begins with it and a dash (a survey tab draws a section per staff
 * group, `research-group-…`). The sections above it are read after it and push it down, so it is
 * held at the top until they settle, or the reader scrolls, presses a key or clicks. Once per
 * fragment; the section nav still only scrolls.
 */
function useLandOnHash(hash: string) {
  React.useEffect(() => {
    const id = decodeURIComponent(hash.replace(/^#/, ''));
    if (!id) return;
    const started = Date.now();
    let found = 0;
    let frame = 0;
    let stopped = false;
    const stop = () => {
      stopped = true;
    };
    const events = ['wheel', 'touchmove', 'keydown', 'mousedown'] as const;
    for (const name of events) window.addEventListener(name, stop, { passive: true, once: true });
    const look = () => {
      if (stopped) return;
      const target =
        document.getElementById(id) ?? document.querySelector<HTMLElement>(`[id^="${CSS.escape(id)}-"]`);
      if (target) {
        found ||= Date.now();
        // `scroll-mt` on the section keeps it clear of the sticky bars.
        if (Math.abs(target.getBoundingClientRect().top) > 120) target.scrollIntoView({ block: 'start' });
        if (Date.now() - found < HASH_HOLD_MS) frame = window.requestAnimationFrame(look);
        return;
      }
      if (Date.now() - started < HASH_WAIT_MS) frame = window.requestAnimationFrame(look);
    };
    frame = window.requestAnimationFrame(look);
    return () => {
      window.cancelAnimationFrame(frame);
      for (const name of events) window.removeEventListener(name, stop);
    };
  }, [hash]);
}
